import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { fmtDate } from "../../lib/dateFormat";
import { useI18n } from "../../contexts/I18nContext";
import { Car, Users, Calendar as CalIcon, CheckCircle, Clock, XCircle, TrendingUp, FileWarning, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import PushNotificationToggle from "../../components/PushNotificationToggle";
import AdvancedAnalytics from "./AdvancedAnalytics";

const DOC_LABELS = {
  rca: "RCA",
  itp: "ITP",
  rovigneta: "Rovinieta",
  casco: "CASCO",
};
const STATUS_COLORS = {
  expired: { bg: "#7f1d1d", text: "#fef2f2", label: "EXPIRAT" },
  critical: { bg: "#9a3412", text: "#fff7ed", label: "EXPIRA <7 ZILE" },
  warning: { bg: "#a16207", text: "#fefce8", label: "ATENTIE" },
  ok: { bg: "var(--brand-surface-elevated)", text: "var(--brand-text)", label: "OK" },
};

function MaintenanceAlertsWidget() {
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    api.get("/admin/documents/expiring", { params: { days_ahead: 60 } }).then((r) => setDocs(r.data)).catch(() => setDocs([]));
  }, []);
  if (docs === null) return null;
  const urgent = docs.filter((d) => d.status === "expired" || d.status === "critical");
  const warning = docs.filter((d) => d.status === "warning");
  return (
    <div className="card-surface p-6 mb-12" data-testid="maintenance-alerts-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileWarning className="w-5 h-5" style={{ color: urgent.length > 0 ? "#ef4444" : "var(--brand-primary)" }} />
          <h2 className="font-serif text-xl">Alerte mentenanta flota</h2>
          <span className="text-xs px-2 py-0.5 rounded" style={{ background: "var(--brand-surface-elevated)", color: "var(--brand-text-secondary)" }}>{docs.length} in monitorizare</span>
        </div>
        <Link to="/admin/documents" className="text-xs underline" style={{ color: "var(--brand-primary)" }} data-testid="maintenance-view-all">Vezi toate →</Link>
      </div>
      {docs.length === 0 ? (
        <p className="text-sm flex items-center gap-2" style={{ color: "var(--brand-text-secondary)" }}>
          <CheckCircle className="w-4 h-4" style={{ color: "var(--brand-primary)" }} /> Niciun document care expira in urmatoarele 60 zile.
        </p>
      ) : (
        <ul className="space-y-2 text-sm">
          {[...urgent, ...warning].slice(0, 6).map((d) => (
            <li key={d.id} className="flex items-center justify-between p-2 rounded" style={{ background: STATUS_COLORS[d.status].bg, color: STATUS_COLORS[d.status].text }} data-testid={`maintenance-row-${d.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                <span className="font-medium truncate">{d.car_name || d.car_brand_model}</span>
                <span className="text-xs opacity-90 shrink-0">— {DOC_LABELS[d.doc_type] || d.doc_type}</span>
              </div>
              <span className="text-[10px] px-1.5 py-0.5 rounded shrink-0 ml-2" style={{ background: "rgba(0,0,0,0.35)" }}>
                {STATUS_COLORS[d.status].label} {d.days_until >= 0 ? `· ${d.days_until}z` : `· acum ${Math.abs(d.days_until)}z`}
              </span>
            </li>
          ))}
          {(urgent.length + warning.length) > 6 && (
            <li className="text-xs text-center pt-1" style={{ color: "var(--brand-text-secondary)" }}>
              ... si inca {(urgent.length + warning.length) - 6} document(e)
            </li>
          )}
          {(urgent.length + warning.length) === 0 && (
            <li className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>Doar documente cu status OK in urmatoarele 60 zile.</li>
          )}
        </ul>
      )}
    </div>
  );
}

export default function AdminAnalytics() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);

  if (!stats) return <div className="text-sm" style={{ color: "var(--brand-text-secondary)" }}>...</div>;

  const cards = [
    { icon: <Car className="w-5 h-5" />, label: "Masini active", value: stats.totals.active_cars, sub: `${stats.totals.cars} total` },
    { icon: <Users className="w-5 h-5" />, label: "Proprietari", value: stats.totals.owners },
    { icon: <Clock className="w-5 h-5" />, label: "Rezervari in asteptare", value: stats.totals.pending, accent: stats.totals.pending > 0 },
    { icon: <CheckCircle className="w-5 h-5" />, label: "Aprobate", value: stats.totals.approved },
    { icon: <XCircle className="w-5 h-5" />, label: "Respinse", value: stats.totals.rejected },
    { icon: <TrendingUp className="w-5 h-5" />, label: "Rata aprobare", value: `${stats.approval_rate}%` },
  ];

  return (
    <div data-testid="admin-analytics">
      <div className="mb-10">
        <div className="label-eyebrow mb-2" style={{ color: "var(--brand-primary)" }}>{t("admin.dashboard")}</div>
        <h1 className="font-serif text-3xl sm:text-4xl">Dashboard</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12" data-testid="stats-cards">
        {cards.map((c, i) => (
          <div key={i} className="card-surface p-6" style={c.accent ? { borderColor: "var(--brand-primary)" } : {}} data-testid={`stat-${i}`}>
            <div className="flex items-center gap-2 label-eyebrow mb-3" style={c.accent ? { color: "var(--brand-primary)" } : {}}>
              {c.icon}{c.label}
            </div>
            <div className="font-serif text-3xl sm:text-4xl" style={{ color: c.accent ? "var(--brand-primary)" : "var(--brand-text)" }}>
              {c.value}
            </div>
            {c.sub && <div className="text-xs mt-1" style={{ color: "var(--brand-text-secondary)" }}>{c.sub}</div>}
          </div>
        ))}
      </div>

      <MaintenanceAlertsWidget />
      <PushNotificationToggle />

      <div className="mt-12 mb-6">
        <AdvancedAnalytics />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-12">
        <div className="card-surface p-6">
          <h2 className="font-serif text-2xl mb-4">Top 5 masini</h2>
          {stats.top_cars.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--brand-text-secondary)" }}>Nu exista rezervari inca.</p>
          ) : (
            <ul className="space-y-3">
              {stats.top_cars.map((c, i) => (
                <li key={c.car_id} className="flex items-center justify-between" data-testid={`top-car-${i}`}>
                  <div>
                    <div className="label-eyebrow text-[10px]">{c.brand}</div>
                    <div className="text-sm">{c.name}</div>
                  </div>
                  <span className="font-serif text-2xl" style={{ color: "var(--brand-primary)" }}>{c.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card-surface p-6">
          <h2 className="font-serif text-2xl mb-4">Trend 30 zile</h2>
          {stats.timeline_30d.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--brand-text-secondary)" }}>Nu exista date.</p>
          ) : (
            <div className="flex items-end gap-1 h-32" data-testid="timeline-chart">
              {stats.timeline_30d.map((d) => {
                const max = Math.max(...stats.timeline_30d.map((x) => x.count));
                const h = (d.count / max) * 100;
                return (
                  <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day}: ${d.count}`}>
                    <div className="w-full" style={{ height: `${h}%`, background: "var(--brand-primary)", minHeight: "2px" }} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="card-surface p-6">
        <h2 className="font-serif text-2xl mb-4">Activitate recenta</h2>
        {stats.recent.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--brand-text-secondary)" }}>Nicio rezervare</p>
        ) : (
          <ul className="space-y-2">
            {stats.recent.map((r) => (
              <li key={r.id} className="flex items-center justify-between text-sm border-b py-2" style={{ borderColor: "var(--brand-border)" }} data-testid={`recent-${r.id}`}>
                <div>
                  <span style={{ color: "var(--brand-primary)" }}>{r.guest_name}</span>
                  <span style={{ color: "var(--brand-text-secondary)" }}> a rezervat </span>
                  <span>{r.car_name}</span>
                </div>
                <div className="flex items-center gap-3 text-xs" style={{ color: "var(--brand-text-secondary)" }}>
                  <span>{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</span>
                  <span style={{ color: r.status === "approved" ? "var(--brand-primary)" : r.status === "rejected" ? "#ef4444" : undefined }}>
                    {t(`common.${r.status}`)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
